/**
 * License Plate Input Component
 *
 * Auto-uppercase input for Dutch license plates.
 * Shows loading state and validation hints.
 * Handles paste with normalization and visual formatting.
 */

import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatPlateDisplay, normalizePlateInput } from '../lib/format';

interface PlateInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
  autoFocus?: boolean;
  retryDisabled?: boolean;
  retryCountdown?: number;
}

/**
 * Check if a plate has valid format (6 alphanumeric chars)
 */
function isValidFormat(plate: string): boolean {
  const normalized = normalizePlateInput(plate);
  return /^[A-Z0-9]{6}$/.test(normalized);
}

export function PlateInput({
  value,
  onChange,
  onSubmit,
  isLoading,
  error,
  autoFocus = true,
  retryDisabled = false,
  retryCountdown = 0,
}: PlateInputProps) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [showShake, setShowShake] = useState(false);

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure dialog is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  // Format the display value with dashes
  const displayValue = formatPlateDisplay(value);
  const normalizedValue = normalizePlateInput(value);
  const hasValidFormat = normalizedValue.length === 0 || isValidFormat(value);
  const canSubmit = normalizedValue.length === 6 && !isLoading && !retryDisabled;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canSubmit) {
        onSubmit();
      } else if (normalizedValue.length > 0 && !hasValidFormat) {
        // Shake animation for invalid input
        setShowShake(true);
        setTimeout(() => setShowShake(false), 500);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Normalize: remove non-alphanumeric, uppercase, limit to 6 chars
    const normalized = normalizePlateInput(e.target.value).slice(0, 6);
    onChange(normalized);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    // Normalize pasted content (handles "NL AB 123 CD", "ab-123-cd", etc.)
    const normalized = normalizePlateInput(pasted).slice(0, 6);
    onChange(normalized);
  };

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={t('calculator.platePlaceholder', 'Kenteken (bijv. AB-123-CD)')}
            className={cn(
              'font-mono text-base uppercase tracking-wider pr-10',
              error && 'border-destructive focus-visible:ring-destructive',
              showShake && 'animate-shake'
            )}
            disabled={isLoading}
            autoComplete="off"
            spellCheck={false}
            maxLength={8} // XX-XX-XX format
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
        <Button type="button" onClick={handleSubmit} disabled={!canSubmit} size="default">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : retryDisabled && retryCountdown > 0 ? (
            <span className="tabular-nums">{retryCountdown}s</span>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              {t('calculator.calculate', 'Bereken')}
            </>
          )}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!error && normalizedValue.length > 0 && normalizedValue.length < 6 && (
        <p className="text-xs text-muted-foreground">
          {t('calculator.plateIncomplete', 'Voer een volledig kenteken in (6 tekens)')}
        </p>
      )}
      {!error && normalizedValue.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {t('calculator.plateHint', 'Druk op Enter of klik Bereken om gegevens op te halen')}
        </p>
      )}
    </div>
  );
}
